import React, { useState } from 'react';
import api from './api';

export const SchoolDetailModal = ({ school, onClose, onEdit, language, token, onRefresh }) => {
  const [enriching, setEnriching] = useState(false);
  const [enrichResult, setEnrichResult] = useState(null);

  if (!school) return null;

  const enrichFromGoogle = async () => {
    setEnriching(true);
    setEnrichResult(null);
    try {
      const schoolId = school.udise_code || school.affiliation_number || school._id;
      const res = await api.post(`/api/school-finder/enrich-from-google/${schoolId}`, {}, token);
      setEnrichResult(res);
      if (res.success && onRefresh) {
        setTimeout(() => onRefresh(), 1000);
      }
    } catch (err) {
      setEnrichResult({ success: false, message: 'Error fetching from Google' });
    }
    setEnriching(false);
  };

  const DetailRow = ({ icon, label, value, isLink, linkType }) => {
    if (!value || value === 'NA') return null;
    
    let content = value;
    if (isLink && linkType === 'phone') {
      content = <a href={`tel:${value}`} className="text-blue-600 hover:underline">{value}</a>;
    } else if (isLink && linkType === 'whatsapp') {
      content = <a href={`https://wa.me/91${value}`} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline flex items-center gap-1">{value} <span className="text-xs">↗</span></a>;
    } else if (isLink && linkType === 'email') {
      content = <a href={`mailto:${value}`} className="text-purple-600 hover:underline">{value}</a>;
    } else if (isLink && linkType === 'website') {
      content = <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{value}</a>;
    }
    
    return (
      <div className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
        <span className="text-lg">{icon}</span>
        <div className="flex-1">
          <p className="text-xs text-gray-500">{label}</p>
          <p className="text-sm font-medium text-gray-800">{content}</p>
        </div>
      </div>
    );
  };

  const Section = ({ title, icon, children }) => (
    <div className="mb-5">
      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
        {icon} {title}
      </h3>
      <div className="bg-gray-50 rounded-xl p-3">
        {children}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-5">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <h2 className="text-xl font-bold">{school.school_name}</h2>
              {school.udise_code && <p className="text-orange-100 text-sm mt-1">UDISE: {school.udise_code}</p>}
              <div className="flex flex-wrap gap-2 mt-3">
                {school.board && <span className="px-2 py-1 bg-white/20 rounded text-xs">{school.board}</span>}
                {school.school_management && <span className="px-2 py-1 bg-white/20 rounded text-xs">{school.school_management}</span>}
                {school.school_category && <span className="px-2 py-1 bg-white/20 rounded text-xs">{school.school_category}</span>}
                {school.location_type && <span className="px-2 py-1 bg-blue-400/30 rounded text-xs">📍 {school.location_type}</span>}
                {school.udise_full_data && <span className="px-2 py-1 bg-green-400/30 rounded text-xs">✅ Full Data</span>}
                {school.has_contact && <span className="px-2 py-1 bg-green-400/30 rounded text-xs">📱 Contact</span>}
              </div>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white text-2xl ml-4">✕</button>
          </div>
        </div>

        {/* Content - Two Column Layout */}
        <div className="p-5 overflow-y-auto max-h-[60vh]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left Column */}
            <div>
              {/* Contact Section */}
              <Section title={language === 'hi' ? 'संपर्क जानकारी' : 'Contact Information'} icon="📞">
                <DetailRow icon="💬" label="WhatsApp" value={school.whatsapp_number} isLink linkType="whatsapp" />
                <DetailRow icon="📱" label="Phone" value={school.phone || school.mobile_number} isLink linkType="phone" />
                <DetailRow icon="📧" label="Email" value={school.email} isLink linkType="email" />
                <DetailRow icon="🌐" label="Website" value={school.website} isLink linkType="website" />
              </Section>

              {/* Principal Section */}
              <Section title={language === 'hi' ? 'प्रिंसिपल' : 'Principal'} icon="👤">
                <DetailRow icon="👨‍🏫" label={language === 'hi' ? 'प्रिंसिपल' : 'Principal Name'} value={school.principal_name} />
                <DetailRow icon="🎓" label={language === 'hi' ? 'योग्यता' : 'Qualification'} value={school.principal_qualification} />
                <DetailRow icon="👤" label={language === 'hi' ? 'Respondent' : 'Respondent'} value={school.respondent_name} />
              </Section>

              {/* Location Section */}
              <Section title={language === 'hi' ? 'स्थान' : 'Location'} icon="📍">
                <DetailRow icon="🏠" label={language === 'hi' ? 'पता' : 'Address'} value={school.address} />
                <DetailRow icon="🏘️" label={language === 'hi' ? 'गांव' : 'Village'} value={school.village} />
                <DetailRow icon="🏙️" label={language === 'hi' ? 'जिला' : 'District'} value={school.district} />
                <DetailRow icon="📦" label={language === 'hi' ? 'ब्लॉक' : 'Block'} value={school.block} />
                <DetailRow icon="🗺️" label={language === 'hi' ? 'राज्य' : 'State'} value={school.state} />
                <DetailRow icon="📮" label="Pincode" value={school.pincode} />
                {school.latitude && (
                  <DetailRow icon="🎯" label="GPS" value={`${school.latitude}, ${school.longitude}`} />
                )}
              </Section>
            </div>

            {/* Right Column */}
            <div>
              {/* School Details Section */}
              <Section title={language === 'hi' ? 'स्कूल विवरण' : 'School Details'} icon="🏫">
                <DetailRow icon="🔢" label="UDISE Code" value={school.udise_code} />
                <DetailRow icon="📋" label={language === 'hi' ? 'संबद्धता संख्या' : 'Affiliation No.'} value={school.affiliation_number} />
                <DetailRow icon="📅" label={language === 'hi' ? 'स्थापना वर्ष' : 'Est. Year'} value={school.year_of_foundation} />
                <DetailRow icon="🏛️" label={language === 'hi' ? 'प्रबंधन' : 'Management'} value={school.school_management} />
                <DetailRow icon="📚" label={language === 'hi' ? 'श्रेणी' : 'Category'} value={school.school_category} />
                <DetailRow icon="👫" label={language === 'hi' ? 'प्रकार' : 'Type'} value={school.school_coed_type} />
                <DetailRow icon="🗣️" label={language === 'hi' ? 'माध्यम' : 'Medium'} value={Array.isArray(school.medium_of_instruction) ? school.medium_of_instruction.join(', ') : school.medium_of_instruction || school.medium} />
                <DetailRow icon="📖" label={language === 'hi' ? 'कक्षाएं' : 'Classes'} value={school.class_from && school.class_to ? `${school.class_from} - ${school.class_to}` : null} />
              </Section>

              {/* Board & Affiliation */}
              <Section title={language === 'hi' ? 'बोर्ड संबद्धता' : 'Board Affiliation'} icon="🎓">
                <DetailRow icon="📜" label={language === 'hi' ? 'सेकेंडरी बोर्ड' : 'Secondary Board'} value={school.board_sec} />
                <DetailRow icon="📜" label={language === 'hi' ? 'हायर सेकेंडरी' : 'Higher Secondary'} value={school.board_hsec} />
                <DetailRow icon="📅" label={language === 'hi' ? 'Affiliation From' : 'Affiliation From'} value={school.affiliation_from} />
                <DetailRow icon="📅" label={language === 'hi' ? 'Affiliation To' : 'Affiliation To'} value={school.affiliation_to} />
              </Section>

              {/* Statistics */}
              {(school.total_students || school.total_teachers) && (
                <Section title={language === 'hi' ? 'आंकड़े' : 'Statistics'} icon="📊">
                  <DetailRow icon="👥" label={language === 'hi' ? 'कुल छात्र' : 'Total Students'} value={school.total_students?.toLocaleString()} />
                  <DetailRow icon="👦" label={language === 'hi' ? 'लड़के' : 'Boys'} value={school.total_boys?.toLocaleString()} />
                  <DetailRow icon="👧" label={language === 'hi' ? 'लड़कियां' : 'Girls'} value={school.total_girls?.toLocaleString()} />
                  <DetailRow icon="👨‍🏫" label={language === 'hi' ? 'कुल शिक्षक' : 'Total Teachers'} value={school.total_teachers?.toLocaleString()} />
                  <DetailRow icon="📋" label={language === 'hi' ? 'Regular Teachers' : 'Regular'} value={school.teachers_regular?.toLocaleString()} />
                  <DetailRow icon="📋" label={language === 'hi' ? 'Contract Teachers' : 'Contract'} value={school.teachers_contract?.toLocaleString()} />
                </Section>
              )}

              {/* Additional Info */}
              <Section title={language === 'hi' ? 'अतिरिक्त जानकारी' : 'Additional Info'} icon="ℹ️">
                <DetailRow icon="🏛️" label="Residential Type" value={school.residential_type} />
                <DetailRow icon="🚸" label="CWSN School" value={school.cwsn_school} />
                <DetailRow icon="🌟" label="PM SHRI School" value={school.pm_shri_school ? 'Yes' : null} />
                <DetailRow icon="🔄" label="Shift School" value={school.shift_school} />
                <DetailRow icon="📅" label="Academic Year" value={school.academic_year} />
              </Section>
              
              {/* Infrastructure Section */}
              <Section title={language === 'hi' ? 'बुनियादी ढांचा' : 'Infrastructure'} icon="🏗️">
                <DetailRow icon="🏟️" label={language === 'hi' ? 'खेल का मैदान' : 'Playground'} value={school.has_playground === true ? '✅ Yes' : school.has_playground === false ? '❌ No' : null} />
                <DetailRow icon="📚" label={language === 'hi' ? 'पुस्तकालय' : 'Library'} value={school.has_library === true ? '✅ Yes' : school.has_library === false ? '❌ No' : null} />
                <DetailRow icon="💻" label={language === 'hi' ? 'कंप्यूटर लैब' : 'Computer Lab'} value={school.has_computer_lab === true ? '✅ Yes' : school.has_computer_lab === false ? '❌ No' : null} />
                <DetailRow icon="⚡" label={language === 'hi' ? 'बिजली' : 'Electricity'} value={school.has_electricity === true ? '✅ Yes' : school.has_electricity === false ? '❌ No' : null} />
                <DetailRow icon="💧" label={language === 'hi' ? 'पीने का पानी' : 'Drinking Water'} value={school.has_drinking_water === true ? '✅ Yes' : school.has_drinking_water === false ? '❌ No' : null} />
                <DetailRow icon="🚽" label={language === 'hi' ? 'शौचालय' : 'Toilets'} value={school.has_toilet === true ? '✅ Yes' : school.has_toilet === false ? '❌ No' : null} />
                <DetailRow icon="🚺" label={language === 'hi' ? 'लड़कियों का शौचालय' : 'Girls Toilet'} value={school.has_girls_toilet === true ? '✅ Yes' : school.has_girls_toilet === false ? '❌ No' : null} />
                <DetailRow icon="♿" label={language === 'hi' ? 'रैंप' : 'Ramp'} value={school.has_ramp === true ? '✅ Yes' : school.has_ramp === false ? '❌ No' : null} />
                <DetailRow icon="🌐" label={language === 'hi' ? 'इंटरनेट' : 'Internet'} value={school.has_internet === true ? '✅ Yes' : school.has_internet === false ? '❌ No' : null} />
              </Section>
            </div>
          </div>

          {/* Source Info */}
          <div className="mt-4 pt-3 border-t text-xs text-gray-400 flex flex-wrap items-center gap-4">
            <span>Source: {school.source || 'N/A'}</span>
            {school.udise_enriched && <span className="text-green-500">✅ UDISE+ Enriched</span>}
            {school.udise_full_data && <span className="text-blue-500">📋 Full Data</span>}
            {school.saras_enriched && <span className="text-purple-500">🎓 SARAS Enriched</span>}
            {school.geo_portal_updated_at && <span>Geo Portal: {new Date(school.geo_portal_updated_at).toLocaleDateString()}</span>}
          </div>
        </div>

        {/* Enrich Result Toast */}
        {enrichResult && (
          <div className={`mx-4 mb-2 p-3 rounded-lg ${enrichResult.success ? 'bg-green-100 border border-green-300' : 'bg-red-100 border border-red-300'}`}>
            <p className={`text-sm font-medium ${enrichResult.success ? 'text-green-800' : 'text-red-800'}`}>
              {enrichResult.success ? '✅' : '❌'} {enrichResult.message_hi || enrichResult.message}
            </p>
            {enrichResult.success && enrichResult.updated_fields && enrichResult.updated_fields.length > 0 && (
              <p className="text-xs text-green-600 mt-1">
                {language === 'hi' ? 'अपडेट हुए:' : 'Updated:'} {enrichResult.updated_fields.join(', ')}
              </p>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="border-t p-4 flex justify-between items-center bg-gray-50">
          <div className="flex gap-2 flex-wrap">
            {school.whatsapp_number && (
              <a 
                href={`https://wa.me/91${school.whatsapp_number}?text=${encodeURIComponent('नमस्ते! शिक्षा मित्र की ओर से।')}`}
                target="_blank" 
                rel="noopener noreferrer"
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2 text-sm"
                data-testid="whatsapp-link"
              >
                💬 WhatsApp
              </a>
            )}
            {school.email && (
              <a 
                href={`mailto:${school.email}`}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 flex items-center gap-2 text-sm"
                data-testid="email-link"
              >
                📧 Email
              </a>
            )}
            {school.latitude && (
              <a 
                href={`https://www.google.com/maps?q=${school.latitude},${school.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2 text-sm"
                data-testid="map-link"
              >
                📍 Map
              </a>
            )}
            {/* Google Enrich Button */}
            <button
              onClick={enrichFromGoogle}
              disabled={enriching}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-wait flex items-center gap-2 text-sm"
              data-testid="google-enrich-btn"
            >
              {enriching ? (
                <>
                  <span className="animate-spin">⏳</span>
                  {language === 'hi' ? 'खोज रहे हैं...' : 'Searching...'}
                </>
              ) : (
                <>
                  🔍 {language === 'hi' ? 'Google से Contact लाएं' : 'Fetch from Google'}
                </>
              )}
            </button>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={() => { onEdit(school); onClose(); }}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-sm"
              data-testid="edit-school-btn"
            >
              ✏️ Edit
            </button>
            <button 
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
              data-testid="close-modal-btn"
            >
              {language === 'hi' ? 'बंद करें' : 'Close'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchoolDetailModal;
